
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { AdBanner, AdPosition, AdFrequency, User } from '../types';
import { X, ExternalLink, Megaphone, Sparkles, TrendingUp, Zap, ChevronRight } from 'lucide-react';

interface AdDisplayProps {
  position: AdPosition;
  ads: AdBanner[];
  className?: string;
  user?: User | null;
}

interface AdImpressionData {
  adId: string;
  count: number;
  lastShown: number;
  sessionShown?: boolean;
  clicks: number;
  dismissedAt?: number;
  firstSeen?: number;
}

// Optimized Image Component with Skeleton Loading
const AdImage: React.FC<{
  src: string;
  alt?: string;
  className?: string; // Wrapper classes
  imageClassName?: string; // Image specific classes
  style?: React.CSSProperties;
}> = ({ src, alt, className, imageClassName, style }) => {
  const [isLoaded, setIsLoaded] = useState(false);

  return (
    <div className={`relative overflow-hidden bg-slate-100/10 ${className}`} style={{ minHeight: '1rem' }}>
      {!isLoaded && (
        <div className="absolute inset-0 bg-white/10 animate-pulse z-10" />
      )}
      <img
        src={src}
        alt={alt}
        className={`w-full h-full transition-opacity duration-700 ease-in-out ${isLoaded ? 'opacity-100' : 'opacity-0'} ${imageClassName || 'object-cover'}`}
        style={style}
        onLoad={() => setIsLoaded(true)}
      />
    </div>
  );
};

const DISMISS_COOLDOWN_MS = 30 * 60 * 1000; // 30 min cooldown after dismiss

const AdDisplay: React.FC<AdDisplayProps> = ({ position, ads, className, user }) => {
  const [activeAd, setActiveAd] = useState<AdBanner | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);
  const impressionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const viewStartRef = useRef<number>(0);

  // Determine user tier for targeting
  const userTier = useMemo(() => {
    if (!user) return 'free';
    if (user.role === 'admin') return 'max';
    if (user.isPremium) return 'premium';
    return 'free';
  }, [user]);

  const isNewUser = useMemo(() => {
    if (!user?.createdAt) return false;
    return Date.now() - new Date(user.createdAt).getTime() < 7 * 24 * 60 * 60 * 1000;
  }, [user]);

  // Check if ad matches user targeting
  const matchesTargeting = useCallback((ad: AdBanner): boolean => {
    if (!ad.targetAudience || ad.targetAudience === 'all') return true;
    if (ad.targetAudience === 'new_users') return isNewUser;
    if (ad.targetAudience === 'inactive_users') {
      const lastActive = user?.lastLoginAt ? new Date(user.lastLoginAt).getTime() : 0;
      return Date.now() - lastActive > 3 * 24 * 60 * 60 * 1000;
    }
    return ad.targetAudience === userTier;
  }, [userTier, isNewUser, user]);

  // Check if ad should be shown based on frequency settings
  const shouldShowAd = useCallback((ad: AdBanner): boolean => {
    const storageKey = `luca_ad_${ad.id}`;
    const stored = localStorage.getItem(storageKey);
    const now = Date.now();

    let impressionData: AdImpressionData = stored
      ? JSON.parse(stored)
      : { adId: ad.id, count: 0, lastShown: 0, clicks: 0 };

    // Check dismiss cooldown
    if (impressionData.dismissedAt && (now - impressionData.dismissedAt) < DISMISS_COOLDOWN_MS) {
      return false;
    }

    const frequency = ad.frequency || 'every-visit';

    // Check max impressions cap
    if (ad.maxImpressionsPerUser && impressionData.count >= ad.maxImpressionsPerUser) {
      return false;
    }

    // Check targeting
    if (!matchesTargeting(ad)) return false;

    switch (frequency) {
      case 'once':
        return impressionData.count === 0;
      case 'session':
        return !impressionData.sessionShown;
      case 'daily':
        return (now - impressionData.lastShown) >= 24 * 60 * 60 * 1000;
      case 'interval':
        return (now - impressionData.lastShown) >= (ad.intervalMinutes || 60) * 60 * 1000;
      case 'every-interaction':
        return true;
      case 'every-visit':
      default:
        return true;
    }
  }, [matchesTargeting]);

  // Track ad impression
  const trackImpression = useCallback((ad: AdBanner) => {
    const storageKey = `luca_ad_${ad.id}`;
    const stored = localStorage.getItem(storageKey);
    const now = Date.now();

    let impressionData: AdImpressionData = stored
      ? JSON.parse(stored)
      : { adId: ad.id, count: 0, lastShown: 0, clicks: 0 };

    impressionData.count += 1;
    impressionData.lastShown = now;
    impressionData.sessionShown = true;
    if (!impressionData.firstSeen) impressionData.firstSeen = now;

    localStorage.setItem(storageKey, JSON.stringify(impressionData));
    viewStartRef.current = now;
  }, []);

  // Track ad click with view duration
  const trackClick = useCallback((ad: AdBanner) => {
    const storageKey = `luca_ad_${ad.id}`;
    const stored = localStorage.getItem(storageKey);

    let impressionData: AdImpressionData = stored
      ? JSON.parse(stored)
      : { adId: ad.id, count: 0, lastShown: 0, clicks: 0 };

    impressionData.clicks = (impressionData.clicks || 0) + 1;
    localStorage.setItem(storageKey, JSON.stringify(impressionData));

    // Dispatch analytics event
    window.dispatchEvent(new CustomEvent('luca-ad-click', {
      detail: {
        adId: ad.id,
        position: ad.position,
        viewDuration: Date.now() - viewStartRef.current,
        totalImpressions: impressionData.count,
        totalClicks: impressionData.clicks
      }
    }));
  }, []);

  // Track dismiss
  const trackDismiss = useCallback((ad: AdBanner) => {
    const storageKey = `luca_ad_${ad.id}`;
    const stored = localStorage.getItem(storageKey);

    let impressionData: AdImpressionData = stored
      ? JSON.parse(stored)
      : { adId: ad.id, count: 0, lastShown: 0, clicks: 0 };

    impressionData.dismissedAt = Date.now();
    localStorage.setItem(storageKey, JSON.stringify(impressionData));
  }, []);

  useEffect(() => {
    // Clean up impression data for deleted ads (batch for perf)
    const activeAdIds = new Set(ads.map(ad => ad.id));
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith('luca_ad_')) {
        const adId = key.replace('luca_ad_', '');
        if (!activeAdIds.has(adId)) keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(k => localStorage.removeItem(k));

    // 1. Filter valid ads for this position and current time
    const now = new Date();
    const candidates = ads.filter(ad =>
      ad.position === position &&
      ad.isActive &&
      new Date(ad.startDate) <= now &&
      new Date(ad.endDate) >= now &&
      shouldShowAd(ad)
    );

    if (candidates.length === 0) {
      if (activeAd) {
        setIsAnimatingOut(true);
        setTimeout(() => { setActiveAd(null); setIsVisible(false); setIsAnimatingOut(false); }, 300);
      }
      return;
    }

    // 2. Sort by priority (higher first), then select with weighted probability
    const sorted = [...candidates].sort((a, b) => (b.priority || 0) - (a.priority || 0));
    const totalWeight = sorted.reduce((sum, ad) => sum + (ad.probability || 50), 0);
    let roll = Math.random() * totalWeight;
    let selected = sorted[0];
    for (const ad of sorted) {
      roll -= (ad.probability || 50);
      if (roll <= 0) { selected = ad; break; }
    }

    // 3. Preload image if present
    if (selected.imageUrl) {
      const img = new Image();
      img.src = selected.imageUrl;
    }

    // 4. Handle Delay
    impressionTimerRef.current = setTimeout(() => {
      setActiveAd(selected);
      setIsVisible(true);
      setIsAnimatingOut(false);
      trackImpression(selected);
    }, (selected.delaySeconds || 0) * 1000);

    return () => {
      if (impressionTimerRef.current) clearTimeout(impressionTimerRef.current);
    };
  }, [ads, position, shouldShowAd, trackImpression]);

  // Clear session flags on component mount
  useEffect(() => {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith('luca_ad_')) {
        try {
          const data = JSON.parse(localStorage.getItem(key) || '{}');
          if (data.sessionShown) {
            data.sessionShown = false;
            localStorage.setItem(key, JSON.stringify(data));
          }
        } catch { /* skip corrupt entries */ }
      }
    }
  }, []);

  if (!activeAd || !isVisible) return null;

  const handleClose = () => {
    setIsAnimatingOut(true);
    if (activeAd) trackDismiss(activeAd);
    setTimeout(() => { setIsVisible(false); setActiveAd(null); setIsAnimatingOut(false); }, 300);
  };

  const handleAdClick = (url: string) => {
    if (activeAd) trackClick(activeAd);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // Enhanced Design Variants with modern gradients
  const designs: Record<string, string> = {
    sunset: 'bg-gradient-to-br from-amber-500 via-orange-500 to-rose-600 text-white',
    ocean: 'bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700 text-white',
    berry: 'bg-gradient-to-br from-pink-500 via-rose-500 to-fuchsia-600 text-white',
    midnight: 'bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 border border-slate-700 text-white',
    forest: 'bg-gradient-to-br from-emerald-500 via-green-500 to-teal-500 text-white', // Added missing designs
    royal: 'bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 text-white',
    fire: 'bg-gradient-to-br from-red-600 via-orange-600 to-yellow-500 text-white',
    ice: 'bg-gradient-to-br from-cyan-400 via-blue-500 to-indigo-600 text-white',
    neon: 'bg-gradient-to-br from-fuchsia-500 via-pink-500 to-rose-500 text-white',
    emerald: 'bg-gradient-to-br from-teal-400 via-emerald-500 to-green-600 text-white',
    gold: 'bg-gradient-to-br from-yellow-400 via-amber-500 to-orange-600 text-white',
    lavender: 'bg-gradient-to-br from-purple-400 via-violet-500 to-indigo-500 text-white',
    custom: 'bg-slate-800 text-white' // Fallback for custom
  };

  // Custom Style Generation
  const getCustomStyle = (ad: AdBanner, isBackground: boolean = false): React.CSSProperties => {
    const { styleConfig, customColors } = ad;

    // Prioritize styleConfig (New system), fallback to customColors (Old system)
    const baseStyle: React.CSSProperties = {};

    if (styleConfig) {
      if (styleConfig.backgroundColor) baseStyle.backgroundColor = styleConfig.backgroundColor;
      if (styleConfig.textColor) baseStyle.color = styleConfig.textColor;
      if (styleConfig.borderColor) baseStyle.borderColor = styleConfig.borderColor;
      if (styleConfig.borderWidth) baseStyle.borderWidth = `${styleConfig.borderWidth}px`;
      if (styleConfig.borderRadius) baseStyle.borderRadius = `${styleConfig.borderRadius}px`;
      if (styleConfig.shadow && styleConfig.shadow !== 'none') baseStyle.boxShadow = `var(--shadow-${styleConfig.shadow})`;
      if (styleConfig.padding) baseStyle.padding = styleConfig.padding;
    } else if (customColors) {
      if (customColors.background) baseStyle.backgroundColor = customColors.background;
      if (customColors.text) baseStyle.color = customColors.text;
    }

    // Helper to handle image background
    if (isBackground && ad.imageUrl && ad.imagePosition === 'background') {
      baseStyle.backgroundImage = `url(${ad.imageUrl})`;
      baseStyle.backgroundSize = 'cover';
      baseStyle.backgroundPosition = 'center';
    }

    return baseStyle;
  };

  // Get base classes - if custom styling is used, we might want to remove some default tailwind classes
  const getBaseClass = (design: string) => {
    // If it's a known preset design, return its classes
    // If it's 'custom' or unknown, return minimal or empty classes so inline styles take over
    return designs[design] || '';
  };

  const bgClass = activeAd.design === 'custom' ? '' : (designs[activeAd.design] || designs.midnight);
  const customStyle = getCustomStyle(activeAd);

  // Text Color overrides
  const titleStyle = activeAd.styleConfig?.titleColor ? { color: activeAd.styleConfig.titleColor } : {};
  const contentStyle = activeAd.styleConfig?.contentColor ? { color: activeAd.styleConfig.contentColor } : {};

  // Marquee Animation Style
  const marqueeStyle = `
    @keyframes marquee {
      0% { transform: translateX(100%); }
      100% { transform: translateX(-100%); }
    }
    .animate-marquee {
      display: inline-block;
      white-space: nowrap;
      animation: marquee 20s linear infinite;
      padding-left: 100%; /* Start off-screen */
    }
  `;

  // Render based on position
  if (position === 'popup') {
    return (
      <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-md animate-in fade-in duration-300">
        <div
          className={`relative w-full max-w-lg p-10 rounded-3xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-500 ${bgClass}`}
          style={customStyle}
        >
          {/* Enhanced Decorative Elements - Hide if custom background is set to avoid clashes */}
          {!activeAd.styleConfig?.backgroundColor && activeAd.design !== 'custom' && (
            <>
              <div className="absolute top-0 right-0 w-40 h-40 bg-white opacity-20 rounded-full blur-3xl -mr-16 -mt-16 animate-pulse"></div>
              <div className="absolute bottom-0 left-0 w-32 h-32 bg-white opacity-10 rounded-full blur-2xl -ml-12 -mb-12"></div>

              {/* Animated sparkles background */}
              <div className="absolute inset-0 opacity-30 pointer-events-none">
                <Sparkles className="absolute top-8 right-12 text-white animate-pulse" size={16} />
                <Sparkles className="absolute bottom-12 left-16 text-white animate-pulse" size={12} style={{ animationDelay: '1s' }} />
                <Zap className="absolute top-16 left-8 text-white animate-pulse" size={14} style={{ animationDelay: '0.5s' }} />
              </div>
            </>
          )}

          {activeAd.imageUrl && activeAd.imagePosition === 'background' && (
            <div className="absolute inset-0 bg-black/40 z-0"></div>
          )}

          <button onClick={handleClose} className="absolute top-5 right-5 text-current opacity-70 hover:opacity-100 bg-white/10 hover:bg-white/20 rounded-full p-2 transition-all hover:scale-110 z-20">
            <X size={20} />
          </button>

          <div className="relative z-10 flex flex-col items-center text-center">
            {/* Images Logic */}
            {activeAd.imageUrl && activeAd.imagePosition === 'top' && (
              <AdImage src={activeAd.imageUrl} alt="Ad Visual" className="w-full h-48 rounded-xl mb-6 shadow-md" />
            )}

            <div className="mb-5 flex items-center gap-2 text-current opacity-90 text-xs font-bold uppercase tracking-wider">
              <div className="p-1.5 bg-white/20 rounded-lg backdrop-blur-sm">
                <Megaphone size={14} />
              </div>
              <span>Sponsored</span>
            </div>

            <div className="flex w-full gap-5 items-center">
              {activeAd.imageUrl && activeAd.imagePosition === 'left' && (
                <AdImage src={activeAd.imageUrl} alt="Ad Visual" className="w-1/3 h-40 rounded-xl shadow-md" />
              )}

              <div className="flex-1">
                <h3 className="text-3xl font-bold mb-4 drop-shadow-sm" style={titleStyle}>{activeAd.title}</h3>
                <p className="text-current opacity-95 text-base mb-8 leading-relaxed drop-shadow-sm" style={contentStyle}>{activeAd.content}</p>
              </div>

              {activeAd.imageUrl && activeAd.imagePosition === 'right' && (
                <AdImage src={activeAd.imageUrl} alt="Ad Visual" className="w-1/3 h-40 rounded-xl shadow-md" />
              )}
            </div>

            {activeAd.imageUrl && activeAd.imagePosition === 'bottom' && (
              <AdImage src={activeAd.imageUrl} alt="Ad Visual" className="w-full h-48 rounded-xl mt-4 mb-6 shadow-md" />
            )}

            {activeAd.actionUrl && (
              <a
                href={activeAd.actionUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="group block w-full text-center py-4 px-6 bg-white text-slate-900 font-bold rounded-xl hover:bg-white/95 transition-all shadow-xl hover:shadow-2xl hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-2"
                style={activeAd.styleConfig?.textColor && activeAd.design === 'custom' ? { color: activeAd.styleConfig?.backgroundColor || '#000' } : {}}
              >
                {activeAd.actionLabel || 'Learn More'}
                <TrendingUp size={18} className="group-hover:translate-x-1 transition-transform" />
              </a>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (position === 'header') {
    const fontSizeClass = activeAd.fontSize === 'small' ? 'text-xs' : activeAd.fontSize === 'large' ? 'text-base' : 'text-sm';
    const fontWeightClass = activeAd.fontWeight === 'normal' ? 'font-normal' : activeAd.fontWeight === 'bold' ? 'font-bold' : 'font-semibold';
    const textAlignClass = activeAd.textAlign === 'left' ? 'justify-start' : activeAd.textAlign === 'right' ? 'justify-end' : 'justify-center';

    return (
      <div
        className={`fixed top-3 left-1/2 -translate-x-1/2 max-w-xl py-2.5 px-4 flex items-center gap-3 ${fontSizeClass} ${fontWeightClass} rounded-xl shadow-lg z-50 ${bgClass} ${className} overflow-hidden animate-in fade-in duration-300 w-[95%] sm:w-auto`}
        style={customStyle}
      >
        <style>{marqueeStyle}</style>
        {/* Animated background effect */}
        {activeAd.design !== 'custom' && !activeAd.styleConfig?.backgroundColor && (
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-shimmer" style={{ backgroundSize: '200% 100%' }}></div>
        )}

        <div className={`flex items-center gap-2 relative z-10 flex-1 ${textAlignClass}`}>
          <div className="flex items-center gap-1.5 bg-white/20 px-2 py-0.5 rounded backdrop-blur-sm flex-shrink-0">
            <Sparkles size={10} className="animate-pulse" />
            <span className="font-bold uppercase text-[9px] tracking-wider">Ad</span>
          </div>

          {activeAd.imageUrl && (activeAd.imagePosition === 'left' || !activeAd.imagePosition) && (
            <AdImage src={activeAd.imageUrl} className="w-6 h-6 rounded flex-shrink-0" />
          )}

          <div className="flex-1 overflow-hidden relative" style={{ minWidth: '150px' }}>
            {/* If content is long, use marquee. Otherwise, just show it. Approximate char count check. */}
            <div className={`${activeAd.content.length > 50 ? 'animate-marquee' : 'truncate'}`} style={contentStyle}>
              <span style={titleStyle} className="font-bold mr-2">{activeAd.title}</span>
              {activeAd.content}
            </div>
          </div>

          {activeAd.imageUrl && activeAd.imagePosition === 'right' && (
            <AdImage src={activeAd.imageUrl} className="w-6 h-6 rounded flex-shrink-0" />
          )}

          {activeAd.actionUrl && activeAd.actionLabel && (
            <a
              href={activeAd.actionUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={`flex items-center gap-1 px-2.5 py-1 rounded text-xs font-semibold transition-all flex-shrink-0 ${activeAd.buttonStyle === 'outline'
                ? 'border-2 border-white/50 hover:border-white bg-transparent hover:bg-white/10'
                : activeAd.buttonStyle === 'ghost'
                  ? 'bg-transparent hover:bg-white/20'
                  : 'bg-white/20 hover:bg-white/30'
                }`}
              style={activeAd.design === 'custom' && activeAd.customColors ? {
                backgroundColor: activeAd.buttonStyle === 'solid' ? activeAd.customColors.button : 'transparent',
                color: activeAd.customColors.buttonText,
                borderColor: activeAd.buttonStyle === 'outline' ? activeAd.customColors.button : 'transparent'
              } : {}}
            >
              {activeAd.actionLabel} <ExternalLink size={10} />
            </a>
          )}
        </div>
        <button onClick={handleClose} className="p-1 text-current opacity-70 hover:opacity-100 bg-white/10 hover:bg-white/20 rounded transition-all z-10 flex-shrink-0">
          <X size={12} />
        </button>
      </div>
    );
  }

  if (position === 'toast') {
    return (
      <div
        className={`fixed bottom-6 right-6 z-40 w-96 p-6 rounded-2xl shadow-2xl flex flex-col gap-3 animate-in slide-in-from-bottom-5 duration-500 ${bgClass} ${className} overflow-hidden border-2 border-white/10`}
        style={customStyle}
      >
        {activeAd.imageUrl && activeAd.imagePosition === 'background' && (
          <div className="absolute inset-0 bg-black/40 z-0"></div>
        )}

        {/* Decorative elements - Hide if custom background */}
        {!activeAd.styleConfig?.backgroundColor && activeAd.design !== 'custom' && (
          <>
            <div className="absolute top-0 right-0 w-24 h-24 bg-white opacity-10 rounded-full blur-2xl -mr-8 -mt-8"></div>
            <Sparkles className="absolute top-3 right-3 text-white/30 animate-pulse" size={16} />
          </>
        )}

        <div className="flex justify-between items-start relative z-10">
          <div className="flex items-center gap-3">
            {activeAd.imageUrl && (activeAd.imagePosition === 'left' || activeAd.imagePosition === 'top') && (
              <AdImage src={activeAd.imageUrl} className="w-10 h-10 rounded-lg shadow-sm flex-shrink-0" />
            )}
            <div>
              <h4 className="font-bold text-base flex items-center gap-2">
                {!activeAd.imageUrl && (
                  <div className="p-1.5 bg-white/20 rounded-lg backdrop-blur-sm">
                    <Megaphone size={14} />
                  </div>
                )}
                {activeAd.title}
              </h4>
            </div>
          </div>
          <button onClick={handleClose} className="text-current opacity-70 hover:opacity-100 bg-white/10 hover:bg-white/20 rounded-lg p-1.5 transition-all hover:scale-110">
            <X size={16} />
          </button>
        </div>

        <div className="relative z-10">
          <p className="text-sm opacity-95 leading-relaxed mb-1" style={contentStyle}>{activeAd.content}</p>

          {activeAd.imageUrl && (activeAd.imagePosition === 'bottom' || activeAd.imagePosition === 'right') && (
            <AdImage src={activeAd.imageUrl} className="w-full h-36 rounded-lg shadow-sm mt-3 mb-1" />
          )}
        </div>

        {activeAd.actionUrl && (
          <a
            href={activeAd.actionUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="group text-sm font-bold mt-2 self-start bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg backdrop-blur-sm transition-all flex items-center gap-2 relative z-10 hover:scale-105"
          >
            {activeAd.actionLabel || 'Learn More'}
            <TrendingUp size={14} className="group-hover:translate-x-1 transition-transform" />
          </a>
        )}
      </div>
    );
  }

  if (position === 'sidebar') {
    return (
      <div
        className={`relative mx-3 mt-4 mb-2 p-5 rounded-2xl shadow-xl overflow-hidden group ${bgClass} ${className} border border-white/10`}
        style={customStyle}
      >
        {activeAd.imageUrl && activeAd.imagePosition === 'background' && (
          <div className="absolute inset-0 bg-black/40 z-0"></div>
        )}

        {/* Enhanced decorative elements */}
        {!activeAd.styleConfig?.backgroundColor && activeAd.design !== 'custom' && (
          <>
            <div className="absolute top-0 right-0 w-20 h-20 bg-white opacity-20 rounded-full blur-2xl -mr-8 -mt-8 group-hover:opacity-30 transition-opacity"></div>
            <div className="absolute bottom-0 left-0 w-16 h-16 bg-white opacity-10 rounded-full blur-xl -ml-6 -mb-6"></div>
            <Sparkles className="absolute top-2 left-2 text-white/20 animate-pulse" size={12} />
          </>
        )}

        <button onClick={handleClose} className="absolute top-2 right-2 text-white/50 hover:text-white bg-white/10 hover:bg-white/20 rounded-lg p-1 opacity-0 group-hover:opacity-100 transition-all hover:scale-110 z-20">
          <X size={14} />
        </button>

        <div className="relative z-10">
          {activeAd.imageUrl && activeAd.imagePosition === 'top' && (
            <AdImage src={activeAd.imageUrl} className="w-full h-32 rounded-lg mb-3 shadow-sm" />
          )}

          <div className="flex items-center gap-2 mb-2">
            {!activeAd.imageUrl && (
              <div className="p-1 bg-white/20 rounded backdrop-blur-sm">
                <Megaphone size={12} />
              </div>
            )}
            <h4 className="font-bold text-sm" style={titleStyle}>{activeAd.title}</h4>
          </div>

          {/* Corrected logic for sidebar left/right image to match somewhat closer to preview intent */}
          <div className="flex gap-2">
            {activeAd.imageUrl && activeAd.imagePosition === 'left' && (
              <AdImage src={activeAd.imageUrl} className="w-20 h-20 rounded-lg shadow-sm flex-shrink-0" />
            )}
            <p className="text-[11px] opacity-90 mb-3 leading-relaxed" style={contentStyle}>{activeAd.content}</p>
            {activeAd.imageUrl && activeAd.imagePosition === 'right' && (
              <AdImage src={activeAd.imageUrl} className="w-20 h-20 rounded-lg shadow-sm flex-shrink-0" />
            )}
          </div>

          {activeAd.imageUrl && activeAd.imagePosition === 'bottom' && (
            <AdImage src={activeAd.imageUrl} className="w-full h-32 rounded-lg mb-3 shadow-sm" />
          )}

          {activeAd.actionUrl && (
            <a
              href={activeAd.actionUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="group/btn flex items-center justify-center gap-2 text-center py-2 bg-white/20 hover:bg-white/30 rounded-lg text-xs font-bold transition-all hover:scale-105 backdrop-blur-sm"
            >
              {activeAd.actionLabel || 'Open'}
              <TrendingUp size={12} className="group-hover/btn:translate-x-0.5 transition-transform" />
            </a>
          )}
        </div>
      </div>
    );
  }

  return null;
};

export default AdDisplay;
